import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ContactModal from '../about/components/ContactModal';
import { SEO } from '../../utils/seo';

const HomePage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://martinrichens.art';

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Martin Richens Art - Contemporary Fine Art & Illustration",
    "description": "Martin Richens is a contemporary artist and illustrator creating fine art paintings and editorial illustrations. Explore original artworks, commissioned illustration work, and learn about the artist's creative process.",
    "url": siteUrl,
    "mainEntity": {
      "@type": "Person",
      "name": "Martin Richens",
      "jobTitle": "Artist and Illustrator",
      "description": "Contemporary artist and illustrator specializing in fine art paintings and editorial illustration",
      "url": siteUrl,
      "image": "https://static.readdy.ai/image/bfb5433654973d40d0c52d729e2b895a/353d93135c9b706562d1f85fbe540d2e.jpeg"
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Martin Richens Art - Contemporary Fine Art & Illustration"
        description="Martin Richens is a contemporary artist and illustrator creating fine art paintings and editorial illustrations. Explore original artworks, commissioned illustration work, and learn about the artist's creative process."
        keywords="Martin Richens, fine art, contemporary art, illustration, editorial illustration, oil painting, acrylic painting, art gallery, commissioned artwork"
        canonical={siteUrl}
        ogImage="https://static.readdy.ai/image/bfb5433654973d40d0c52d729e2b895a/e2b8001b50bc10fbdc6093b71b938953.png"
        schema={schema}
      />
      
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-[28px]">
        <div className="w-full">
          <div className="flex flex-col items-center text-center space-y-12">
            <div className="space-y-8">
              <h1 className="text-6xl md:text-7xl font-serif text-gray-900 leading-tight">
                Fine Art
              </h1>
              <Link
                to="/fine-art"
                className="inline-block bg-gray-500 text-white px-10 py-4 rounded-lg text-sm tracking-wider font-semibold hover:bg-gray-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                VIEW COLLECTION
              </Link>
            </div>

            <div className="w-full">
              <div className="shadow-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/bfb5433654973d40d0c52d729e2b895a/3ab26ec32635a2d7f1533ece5f3f3dce.jpeg"
                  alt="Featured Artwork"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Illustration Section */}
      <section className="py-20 px-[28px] bg-white">
        <div className="w-full">
          <div className="text-center mb-16 space-y-8">
            <h2 className="text-6xl md:text-7xl font-serif text-gray-900">Illustration</h2>
            <Link
              to="/illustration"
              className="inline-block bg-gray-500 text-white px-10 py-4 rounded-lg text-sm tracking-wider font-semibold hover:bg-gray-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              VIEW ILLUSTRATIONS
            </Link>
          </div>

          <div className="mb-12">
            <Link to="/illustration" className="group cursor-pointer block">
              <div className="overflow-hidden shadow-lg mb-6 transform transition-all duration-300 group-hover:shadow-2xl">
                <img
                  src="https://static.readdy.ai/image/bfb5433654973d40d0c52d729e2b895a/84b2bef9549ec22cc9200fe623aa687d.jpeg"
                  alt="Illustration 1"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 px-8 md:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-4xl font-serif text-gray-900 mb-6">The Artist</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Martin Richens is a contemporary artist whose work explores the intersection of traditional fine art techniques and modern visual storytelling. With 40 years of experience, his pieces have been sold across Europe and North America.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                His unique approach combines classical painting methods with contemporary themes, creating works that resonate with both traditional art enthusiasts and modern collectors.
              </p>
              
              {/* Image for mobile - shown before button */}
              <div className="md:hidden mb-8 overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/9877694e-4f1b-4316-af58-04c369e3cf9c.jpg"
                  alt="Martin Richens"
                  className="w-full h-auto object-cover"
                />
              </div>
              
              <div className="text-center md:text-left">
                <Link
                  to="/about"
                  className="inline-block bg-gray-500 text-white px-10 py-4 rounded-lg text-sm tracking-wider font-bold hover:bg-gray-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  LEARN MORE
                </Link>
              </div>
            </div>

            {/* Image for desktop - original position */}
            <div className="hidden md:block order-1 md:order-2">
              <div className="overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/9877694e-4f1b-4316-af58-04c369e3cf9c.jpg"
                  alt="Martin Richens"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 px-8 md:px-16 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-serif text-gray-900 mb-6">Get In Touch</h2>
          <p className="text-lg text-gray-600 mb-8">
            For commissions, exhibitions, or general inquiries, please reach out.
          </p>
          <button
            onClick={() => setIsContactModalOpen(true)}
            className="inline-block bg-gray-500 text-white px-12 py-4 rounded-lg text-sm tracking-wider font-semibold hover:bg-gray-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            CONTACT ME
          </button>
        </div>
      </section>

      <Footer />

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </div>
  );
};

export default HomePage;
